import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Button } from '../../../../../../ui';
import { ClockIcon } from '../../../../../../ui/icons/icons';
import { TextArea } from '../../../../../../ui/input/text_area/text_area';
import { convertSecondsToTimeTaken } from '../../../../../../utils/helpers/time';
import {
  AssessmentQuestionInfo,
  UserAssessmentResponse,
} from '../../../../../../utils/types/assessment_types';
import { IS_IOS } from '../../assigned_assessments';

type QuestionAreaProps = {
  questionType: string;
  setCurrentAnswer: (value: string | File | undefined) => void;
  recordedVideo: string | undefined;
  setRecordedVideo: (value: string | undefined) => void;
  currentQuestion: AssessmentQuestionInfo;
  timeRemaining: number | undefined;
  submitAnswer: (
    sampleBlob?: Blob[],
    sampleAudioBlob?: Blob[],
    skip?: boolean,
  ) => void;
  submissionDisabled: boolean;
  loading: boolean;
  uploadStatus: 'initial' | 'loading' | 'success' | 'error';
  setUploadStatus: (
    value:
      | ((
          prevState: 'initial' | 'loading' | 'success' | 'error',
        ) => 'initial' | 'loading' | 'success' | 'error')
      | 'initial'
      | 'loading'
      | 'success'
      | 'error',
  ) => void;
  handleQuestionSubmission: (
    video: File,
    timeExpired: boolean,
  ) => Promise<void>;
  responseUserAssessment: UserAssessmentResponse | undefined;
  handleNavigationResult: (navResponse: any) => void;
  questionCount: number;
  setQuestionCount: (value: ((prevState: number) => number) | number) => void;
  submitNoFile: (timeExpired: boolean) => Promise<void>;
  setRecordedAudio: (
    value:
      | ((prevState: string | undefined) => string | undefined)
      | string
      | undefined,
  ) => void;
  totalQuestions: number;
  isBehavioral: boolean;
  currentAnswer: string | File | undefined;
};

export const QuestionArea = ({
  questionType,
  setCurrentAnswer,
  recordedVideo,
  setRecordedVideo,
  currentQuestion,
  timeRemaining,
  submitAnswer,
  submissionDisabled,
  uploadStatus,
  submitNoFile,
  setRecordedAudio,
  totalQuestions,
  isBehavioral,
  questionCount,
  currentAnswer,
}: QuestionAreaProps) => {
  const liveVideoElement = useRef<HTMLVideoElement>(null);
  const playbackVideoElement = useRef<HTMLVideoElement>(null);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder>();
  const [videoChunks, setVideoChunks] = useState<Blob[]>([]);
  const [recording, setRecording] = useState<boolean>(false);
  const [textAnswer, setTextAnswer] = useState<string>('');
  const hasHandledZero = useRef(false);
  const shouldAcceptChunks = useRef(false);
  const videoChunksRef = useRef<Blob[]>([]);
  const [audioRecorder, setAudioRecorder] = useState<MediaRecorder>();
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const audioChunksRef = useRef<Blob[]>([]);

  const isTimeExpired = timeRemaining !== undefined && timeRemaining < 0;

  useEffect(() => {
    let localStream: MediaStream | null = null;

    const initializeCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true,
        });
        localStream = stream;

        setMediaRecorder(new MediaRecorder(stream));

        const audioStream = new MediaStream(stream.getAudioTracks());
        setAudioRecorder(
          new MediaRecorder(audioStream, {
            mimeType: 'audio/webm',
          }),
        );

        if (liveVideoElement.current) {
          liveVideoElement.current.muted = true;
          liveVideoElement.current.srcObject = stream;
          liveVideoElement.current.autoplay = true;
        }
      } catch (err) {
        console.error('Failed to get media devices:', err);
      }
    };

    initializeCamera();

    return () => {
      if (localStream) {
        localStream.getTracks().forEach((track) => track.stop());
      }
      if (liveVideoElement.current) {
        liveVideoElement.current.srcObject = null;
      }
    };
  }, [questionType, currentQuestion.id]); // 👈 Add currentQuestion.id here

  useEffect(() => {
    if (audioRecorder) {
      audioRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0 && shouldAcceptChunks.current) {
          audioChunksRef.current.push(event.data);
          setAudioChunks((prev) => [...prev, event.data]);
        }
      };
    }

    return () => {
      if (audioRecorder) {
        audioRecorder.ondataavailable = null;
      }
    };
  }, [audioRecorder]);

  // Reset local text state when question changes
  useEffect(() => {
    setTextAnswer('');
  }, [currentQuestion.id]);

  // Re-initialize recorders for new questions if they were cleared
  // useEffect(() => {
  //     if (
  //         liveVideoElement.current?.srcObject &&
  //         !mediaRecorder &&
  //         questionType !== 'Classic'
  //     ) {
  //         const stream = liveVideoElement.current.srcObject as MediaStream;
  //         setMediaRecorder(new MediaRecorder(stream));
  //         const audioStream = new MediaStream(stream.getAudioTracks());
  //         setAudioRecorder(
  //             new MediaRecorder(audioStream, {
  //                 mimeType: 'audio/webm',
  //             }),
  //         );
  //     }
  // }, [currentQuestion.id, !!mediaRecorder, questionType]);

  useEffect(() => {
    if (mediaRecorder) {
      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0 && shouldAcceptChunks.current) {
          videoChunksRef.current.push(event.data);
          setVideoChunks((prev) => [...prev, event.data]);
        }
      };
    }

    // Cleanup function to remove handler when mediaRecorder changes
    return () => {
      if (mediaRecorder) {
        mediaRecorder.ondataavailable = null;
      }
    };
  }, [mediaRecorder]);

  useEffect(() => {
    if (
      timeRemaining !== undefined &&
      timeRemaining === 0 &&
      videoChunks.length === 0
    ) {
      submitNoFile(true);
      return;
    }

    if (timeRemaining !== -1) {
      hasHandledZero.current = false;
      return;
    }

    if (hasHandledZero.current) return;
    hasHandledZero.current = true;

    if (recording) {
      stopRecording();
    } else if (submissionDisabled) {
      submitNoFile(true);
    }
  }, [questionType, timeRemaining, recording]);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    if (!recording) {
      startRecording();
    }
    if (isTimeExpired) return;
    let answer = event.target.value;
    setTextAnswer(answer);
    setCurrentAnswer(answer);
  };

  const startRecording = () => {
    // Ensure recorders exist (might have been cleared by previous stop)
    let currentVideoRecorder = mediaRecorder;
    let currentAudioRecorder = audioRecorder;

    if (!currentVideoRecorder || !currentAudioRecorder) {
      if (liveVideoElement.current?.srcObject) {
        const stream = liveVideoElement.current.srcObject as MediaStream;
        currentVideoRecorder = new MediaRecorder(stream);
        const audioStream = new MediaStream(stream.getAudioTracks());
        currentAudioRecorder = new MediaRecorder(audioStream, {
          mimeType: 'audio/webm',
        });
        setMediaRecorder(currentVideoRecorder);
        setAudioRecorder(currentAudioRecorder);
      } else {
        console.error('No stream available to start recording');
        return;
      }
    }

    if (currentVideoRecorder && currentAudioRecorder) {
      videoChunksRef.current = [];
      setVideoChunks([]);
      audioChunksRef.current = [];
      setAudioChunks([]);
      shouldAcceptChunks.current = true;

      currentVideoRecorder.start(10);
      currentAudioRecorder.start(10);
      setRecording(true);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && audioRecorder) {
      shouldAcceptChunks.current = false;
      mediaRecorder.stop();
      audioRecorder.stop();
      setRecording(false);

      setTimeout(() => {
        storeRecording();
        setMediaRecorder(undefined);
        setAudioRecorder(undefined);
      }, 200);
    }
  };

  const storeRecording = () => {
    const videoBlob = new Blob(videoChunksRef.current, {
      type: 'video/webm; codecs=vp8',
    });
    const videoFile = new File([videoBlob], 'sample.webm', {
      type: 'video/webm',
    });

    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
    const audioFile = new File([audioBlob], 'audio.webm', {
      type: 'audio/webm',
    });

    if (questionType !== 'Classic') {
      setCurrentAnswer(videoFile);
    }

    const videoUrl = URL.createObjectURL(videoBlob);
    setRecordedVideo(videoUrl);
    const audioURL = URL.createObjectURL(audioBlob);
    setRecordedAudio(audioURL);

    // Attach to playback element
    if (playbackVideoElement.current) {
      playbackVideoElement.current.src = videoUrl;
      playbackVideoElement.current.load();
      playbackVideoElement.current.autoplay = false;
    }
  };

  const redoRecording = async () => {
    // Reset state but KEEP the camera stream alive
    setRecordedVideo(undefined);
    setCurrentAnswer(undefined);
    videoChunksRef.current = [];
    setVideoChunks([]);
    audioChunksRef.current = [];
    setAudioChunks([]);

    // Clear playback source
    if (playbackVideoElement.current) {
      playbackVideoElement.current.src = '';
    }

    // Re-initialize recorders using the existing stream
    if (liveVideoElement.current?.srcObject) {
      const stream = liveVideoElement.current.srcObject as MediaStream;
      const newMediaRecorder = new MediaRecorder(stream);
      const audioStream = new MediaStream(stream.getAudioTracks());
      const newAudioRecorder = new MediaRecorder(audioStream, {
        mimeType: 'audio/webm',
      });

      setMediaRecorder(newMediaRecorder);
      setAudioRecorder(newAudioRecorder);

      // Start recording immediately
      shouldAcceptChunks.current = true;
      newMediaRecorder.start(10);
      newAudioRecorder.start(10);
      setRecording(true);
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [mediaModalOpen, setMediaModalOpen] = useState(false);

  const handleSubmitting = async (
    sampleBlob?: Blob[],
    sampleAudioBlob?: Blob[],
    skip?: boolean,
  ) => {
    setIsSubmitting(true);
    try {
      // For Classic questions, stop recording first and wait for chunks to flush
      if (questionType === 'Classic' && recording) {
        stopRecording();
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      const res = await submitAnswer(
        questionType === 'Classic' ? videoChunksRef.current : sampleBlob,
        questionType === 'Classic' ? audioChunksRef.current : sampleAudioBlob,
        skip,
      );

      videoChunksRef.current = [];
      setVideoChunks([]);
      audioChunksRef.current = [];
      setAudioChunks([]);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (error) {
      console.error('Submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderRecordButton = () => {
    if (recordedVideo && timeRemaining) {
      return (
        <Button
            onClick={() => redoRecording()}
            variant='secondary'
            label='Redo Recording'
            disabled={
              timeRemaining < 1 || uploadStatus === 'loading' || isSubmitting
            }
            className='text-sm font-bold rounded-xl border-2 border-gray-100 bg-white hover:border-blue-200 hover:bg-blue-50/50 text-gray-700 transition-all duration-300 flex items-center gap-3 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-95 group'
            icon={
              <svg
                  className='w-5 h-5 text-gray-400 group-hover:text-blue-500 group-hover:rotate-180 transition-all duration-500'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2.5}
                    d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                />
              </svg>
            }
        />
      );
    } else {
      return (
        <Button
            onClick={() => (recording ? stopRecording() : startRecording())}
            variant='primary'
            disabled={(recording && videoChunks.length < 2) || isSubmitting}
            label={recording ? 'Stop Recording' : 'Start Recording'}
            className={`text-sm font-bold rounded-xl flex items-center gap-3 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:scale-95 ${
              recording
                ? 'bg-gradient-to-r from-red-600 to-rose-500 text-white shadow-red-200 hover:shadow-red-300'
                : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-blue-200 hover:shadow-blue-300'
            }`}
            icon={
              recording ? (
                <div className='relative w-5 h-5 flex items-center justify-center'>
                  <div className='absolute inset-0 bg-white/20 rounded-full animate-ping' />
                  <div className='w-3 h-3 bg-white rounded-sm' />
                </div>
              ) : (
                <svg className='w-5 h-5' fill='currentColor' viewBox='0 0 24 24'>
                  <path d='M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z' />
                  <path d='M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z' />
                </svg>
              )
            }
        />
      );
    }
  };

  const checkMediaType = () => {
    return !currentQuestion.mediaExtension?.match(/^(mp4|mkv|avi|webm)$/);
  };

  const isImage = checkMediaType();

  /* ─── Full-screen media modal ─── */
  const MediaModal = () => {
    if (!mediaModalOpen || !currentQuestion.fileUrl) return null;
    const handleKey = (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') setMediaModalOpen(false);
    };
    return (
      <div
          className='fixed inset-0 z-[9999] flex items-center justify-center'
          onKeyDown={handleKey}
          tabIndex={0}
          style={{ outline: 'none' }}
      >
        { /* Backdrop */ }
        <div
            className='absolute inset-0 bg-black/80 backdrop-blur-md'
            onClick={() => setMediaModalOpen(false)}
        />

        { /* Content */ }
        <div className='relative z-10 flex flex-col items-center max-w-[95vw] max-h-[95vh] w-full'>
          { /* Header bar */ }
          <div className='w-full flex items-center justify-between px-4 py-2 mb-3'>
            <div className='flex items-center gap-2'>
              <div className='w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 backdrop-blur-sm'>
                { isImage ? (
                  <svg
                      className='w-4 h-4 text-white'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                  >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
                    />
                  </svg>
                ) : (
                  <svg
                      className='w-4 h-4 text-white'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                  >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'
                    />
                  </svg>
                ) }
              </div>
              <span className='text-white/80 text-sm font-medium'>
                { isImage ? 'Image Preview' : 'Video Preview' }
              </span>
              <span className='text-white/40 text-xs bg-white/10 px-2 py-0.5 rounded-full'>
                Reference Material
              </span>
            </div>
            <button
                onClick={() => setMediaModalOpen(false)}
                className='w-9 h-9 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg'
                aria-label='Close preview'
            >
              <svg
                  className='w-5 h-5'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2.5}
                    d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>
          </div>

          { /* Media */ }
          <div className='relative flex items-center justify-center w-full overflow-auto rounded-2xl shadow-2xl bg-black/40 backdrop-blur-sm border border-white/10'>
            { isImage ? (
              <img
                  src={currentQuestion.fileUrl}
                  alt='Full-size preview'
                  className='max-w-full max-h-[80vh] w-auto h-auto object-contain rounded-2xl'
                  style={{ display: 'block' }}
              />
            ) : IS_IOS ? (
              <video
                  className='max-w-full max-h-[80vh] w-auto rounded-2xl'
                  controls
                  autoPlay
                  playsInline
                  muted
                  src={currentQuestion.fileUrl}
              />
            ) : (
              <video
                  className='max-w-full max-h-[80vh] w-auto rounded-2xl'
                  controls
                  autoPlay
                  src={currentQuestion.fileUrl}
              />
            ) }
          </div>

          <p className='mt-3 text-white/40 text-xs'>Click outside to close</p>
        </div>
      </div>
    );
  };

  const renderQuestionMedia = () => {
    if (!currentQuestion.fileUrl) return null;
    return (
      <>
        <MediaModal />
        <div className='w-full space-y-3'>
          { /* Label pill */ }
          <div className='flex items-center gap-2.5 px-3 py-2 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100/80 shadow-sm w-fit'>
            <div className='w-7 h-7 flex items-center justify-center rounded-lg bg-blue-100 shrink-0'>
              { isImage ? (
                <svg
                    className='w-4 h-4 text-blue-600'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
                  />
                </svg>
              ) : (
                <svg
                    className='w-4 h-4 text-blue-600'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'
                  />
                </svg>
              ) }
            </div>
            <div>
              <p className='text-xs font-semibold text-blue-700 leading-none'>
                { isImage ? 'Image' : 'Video' } Reference
              </p>
              <p className='text-[10px] text-blue-400 mt-0.5'>
                Tap to expand full screen
              </p>
            </div>
          </div>

          { /* Media card */ }
          <div
              className='group relative w-full rounded-2xl overflow-hidden shadow-md border border-gray-200/80 bg-gray-950 cursor-pointer'
              style={{ maxHeight: '340px' }}
              onClick={() => setMediaModalOpen(true)}
              role='button'
              aria-label='Expand media preview'
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setMediaModalOpen(true)}
          >
            { isImage ? (
              <img
                  className='w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.02]'
                  style={{ maxHeight: '340px', display: 'block' }}
                  src={currentQuestion.fileUrl}
                  alt={`Question ${currentQuestion.id} media`}
              />
            ) : IS_IOS ? (
              <video
                  className='w-full object-contain'
                  style={{ maxHeight: '340px' }}
                  controls
                  src={currentQuestion.fileUrl}
                  playsInline
                  muted
                  onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <video
                  className='w-full object-contain'
                  style={{ maxHeight: '340px' }}
                  controls
                  src={currentQuestion.fileUrl}
                  onClick={(e) => e.stopPropagation()}
              />
            ) }

            { /* Hover expand overlay — images only (video has native controls) */ }
            { isImage && (
              <div className='absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-between p-3'>
                <span className='text-white text-xs font-medium bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-lg'>
                  Click to expand
                </span>
                <div className='w-9 h-9 flex items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm border border-white/20 text-white shadow-lg hover:bg-white/25 transition-all duration-200'>
                  <svg
                      className='w-5 h-5'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                  >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4'
                    />
                  </svg>
                </div>
              </div>
            ) }

            { /* Expand button badge for video */ }
            { !isImage && (
              <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setMediaModalOpen(true);
                  }}
                  className='absolute top-2 right-2 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm text-white text-[10px] font-semibold px-2.5 py-1.5 rounded-lg border border-white/10 hover:bg-black/80 transition-all duration-200 shadow-lg opacity-0 group-hover:opacity-100'
                  aria-label='Expand video'
              >
                <svg
                    className='w-3.5 h-3.5'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2.5}
                      d='M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4'
                  />
                </svg>
                Full screen
              </button>
            ) }
          </div>
        </div>
      </>
    );
  };

  /* ─── Unified Progress (Shared between Sidebar and Mobile) ─── */
  const renderProgress = () => (
    <div className='bg-white shadow-sm rounded-2xl p-4 border border-blue-50 w-full'>
      <div className='space-y-4'>
        <div className='w-full bg-gray-200 rounded-full h-1.5 overflow-hidden'>
          <div
              className='bg-blue-600 h-full rounded-full transition-all duration-500 ease-out'
              style={{
                width: `${
                  ((totalQuestions - (questionCount - 1)) / totalQuestions) * 100
                }%`,
              }}
          />
        </div>
        <div className='text-center'>
          <div className='flex items-center justify-center gap-2 text-xl font-bold text-gray-900 mb-1 tracking-tight'>
            <ClockIcon className='w-6 h-6 text-blue-600 animate-pulse' />
            { timeRemaining ? convertSecondsToTimeTaken(timeRemaining) : '00:00' }
          </div>
          <p className='text-xs font-medium text-gray-500 uppercase tracking-widest'>
            Remaining Time
          </p>
        </div>
      </div>
    </div>
  );

  /* ─── Unified Sidebar ─── */
  const renderSidebar = () => (
    <div className='flex flex-col gap-3 md:gap-4 h-full overflow-y-auto pr-1 hidden md:flex custom-scrollbar bg-white/50 backdrop-blur-sm rounded-2xl p-3 border border-gray-100'>
      { /* Assessment Progress */ }
      <div className='grid grid-cols-1 gap-4 w-full'>
        { renderProgress() }

        { /* Question Count Card */ }
        { isBehavioral ? (
          <div
              className={
                'w-full mx-auto rounded-2xl border-2 bg-white p-4 text-center transition-all duration-300 shadow-sm border-pink-200 bg-pink-50/30'
              }
          >
            <h3 className='text-xs font-bold text-gray-600 uppercase tracking-widest mb-1'>
              Behavioural type questions may include 3 follow up questions
            </h3>
          </div>
        ) : (
          <div
              className={
                'w-full mx-auto rounded-2xl border-2 bg-white p-4 text-center transition-all duration-300 shadow-sm border-blue-200 bg-blue-50/30'
              }
          >
            <h3 className='text-xs font-bold text-gray-600 uppercase tracking-widest mb-1'>
              Skill Questions
            </h3>
            <div className={'text-xl font-black mb-1text-blue-600'}>
              { questionCount <= 1 ? '0' : questionCount - 1 }
            </div>
            <p className='text-xs font-bold text-gray-500 uppercase tracking-widest'>
              Left to answer
            </p>
          </div>
        ) }
      </div>

      { /* Video Tips */ }
      <div className='bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex-1 min-h-0 overflow-y-auto'>
        <h4 className='text-xs font-bold text-gray-900 uppercase tracking-widest mb-4 flex items-center gap-2'>
          <span className='w-6 h-6 flex items-center justify-center bg-green-100 text-green-700 rounded-lg'>
            <svg
                className='w-4 h-4'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'
              />
            </svg>
          </span>
          Video Tips
        </h4>
        <div className='space-y-3 text-[11px] text-gray-600'>
          <div className='flex items-center gap-2'>
            <div className='w-1.5 h-1.5 rounded-full bg-green-400' />
            <span>Stable internet required</span>
          </div>
          <div className='flex items-center gap-2'>
            <div className='w-1.5 h-1.5 rounded-full bg-green-400' />
            <span>Quiet, well-lit environment</span>
          </div>
          <div className='flex items-center gap-2'>
            <div className='w-1.5 h-1.5 rounded-full bg-green-400' />
            <span className='font-medium text-green-700'>
              Re-recording allowed
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderQuestionArea = () => {
    if (questionType === 'Classic') {
      return (
        <div className='w-full flex flex-col gap-5'>
          { /* Progress bar */ }
          <div className='flex flex-col md:flex-row gap-3'>
            <div className='flex-1'>{ renderProgress() }</div>
            { isBehavioral ? (
              <div className='w-full md:w-auto md:min-w-[200px] mx-auto rounded-2xl border-2 bg-white p-4 text-center transition-all duration-300 shadow-sm border-pink-200 bg-pink-50/30'>
                <h3 className='text-xs font-bold text-gray-600 uppercase tracking-widest mb-1'>
                  Behavioural type questions may include 3 follow up questions
                </h3>
              </div>
            ) : (
              <div className='w-full md:w-auto md:min-w-[160px] mx-auto rounded-2xl border-2 bg-white p-4 text-center transition-all duration-300 shadow-sm border-blue-200 bg-blue-50/30'>
                <h3 className='text-xs font-bold text-gray-600 uppercase tracking-widest mb-1'>
                  Skill Questions
                </h3>
                <div className='text-xl font-black mb-1 text-blue-600'>
                  { questionCount <= 1 ? '0' : questionCount - 1 }
                </div>
                <p className='text-xs font-bold text-gray-500 uppercase tracking-widest'>
                  Left to answer
                </p>
              </div>
            ) }
          </div>

          { /* Main content */ }
          <div
              className={`w-full gap-5 ${
                currentQuestion.fileUrl
                  ? 'grid grid-cols-1 md:grid-cols-2'
                  : 'flex flex-col'
              }`}
          >
            { currentQuestion.fileUrl && renderQuestionMedia() }

            <div className='relative flex-1 flex flex-col gap-4'>
              { /* Textarea card */ }
              <div className='bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-200 focus-within:border-blue-200 focus-within:shadow-md focus-within:shadow-blue-50'>
                { /* Header bar */ }
                <div className='flex items-center justify-between px-4 py-2.5 border-b border-gray-50 bg-gray-50/60'>
                  <span className='text-[11px] font-semibold text-gray-400 uppercase tracking-widest'>
                    Your Answer
                  </span>
                  { isTimeExpired && (
                    <span className='flex items-center gap-1.5 text-[11px] font-semibold text-red-400 bg-red-50 px-2.5 py-1 rounded-full'>
                      <svg
                          className='w-3 h-3'
                          fill='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z' />
                      </svg>
                      Time expired
                    </span>
                  ) }
                </div>

                <TextArea
                    name='answer'
                    value={textAnswer}
                    onChange={handleChange}
                    disabled={isTimeExpired}
                    customClass={`min-h-[220px] w-full border-0 focus:ring-0 resize-none text-gray-800 text-sm leading-relaxed placeholder:text-gray-300 px-4 py-3 transition-colors duration-200 ${
                      isTimeExpired
                        ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                        : 'bg-white'
                    }`}
                />

                { /* Footer with char count + submit */ }
                <div className='flex items-center justify-between px-4 py-3 border-t border-gray-50 bg-gray-50/40'>
                  <span className='text-[11px] text-gray-300 font-medium'>
                    { textAnswer.length } characters
                  </span>

                  <Button
                      onClick={() =>
                        handleSubmitting(
                          videoChunksRef.current,
                          audioChunksRef.current,
                          false,
                        )
                      }
                      variant='primary'
                      label={
                        showSuccess
                          ? '✓ Submitted!'
                          : isSubmitting
                            ? 'Submitting...'
                            : 'Submit Response'
                      }
                      disabled={
                        submissionDisabled ||
                      uploadStatus === 'loading' ||
                      isSubmitting
                      }
                      className={`text-xs font-bold px-6 py-2.5 rounded-xl transition-all duration-200 shadow-sm active:scale-95 ${
                        showSuccess
                          ? 'bg-emerald-500 text-white shadow-emerald-100'
                          : 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:shadow-md hover:shadow-emerald-100 hover:-translate-y-0.5'
                      }`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Modern / Behavioral / Video templates
    return (
      <div className='w-full gap-4 grid grid-cols-1 md:grid-cols-4 items-start'>
        { /* Mobile Progress - Only visible on small screens */ }
        <div className='md:hidden col-span-1'>{ renderProgress() }</div>

        { /* Col 1 (Media) */ }
        <div className={currentQuestion.fileUrl ? 'col-span-2' : 'hidden'}>
          { currentQuestion.fileUrl && renderQuestionMedia() }
        </div>

        { /* Col 2 (Recorder) - Spans 2 cols if no media */ }
        <div
            className={
              currentQuestion.fileUrl
                ? 'border-x col-span-2 border-gray-100 px-4 max-w-3xl'
                : 'col-span-3 border-x border-gray-100 px-4 max-w-3xl'
            }
        >
          <div className='space-y-4'>
            <div className='relative group bg-white rounded-2xl shadow-sm border border-gray-200 p-1'>
              <div className='relative aspect-video rounded-xl overflow-hidden bg-gray-950'>
                <video
                    className={`w-full h-full object-cover transition-opacity duration-500 ${
                      recordedVideo ? 'opacity-0' : 'opacity-100'
                    }`}
                    ref={liveVideoElement}
                    autoPlay
                    playsInline
                    muted
                />
                <video
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                      recordedVideo
                        ? 'opacity-100'
                        : 'opacity-0 pointer-events-none'
                    }`}
                    ref={playbackVideoElement}
                    controls={!!recordedVideo}
                    playsInline
                />
                { recording && (
                  <div className='absolute top-3 right-3 flex items-center gap-2 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-lg text-white text-[10px] font-bold uppercase tracking-wider animate-pulse'>
                    <div className='w-2 h-2 rounded-full bg-red-500' />
                    Live Recording
                  </div>
                ) }
              </div>
            </div>

            <div className='flex flex-wrap justify-center items-center gap-4'>
              { renderRecordButton() }
              { (recordedVideo || questionType === 'Classic') && (
                <Button
                    onClick={() =>
                      handleSubmitting(
                        videoChunksRef.current,
                        audioChunksRef.current,
                        false,
                      )
                    }
                    variant='primary'
                    label={
                      showSuccess
                        ? 'Submitted!'
                        : isSubmitting
                          ? 'Submitting...'
                          : 'Submit Response'
                    }
                    disabled={
                      submissionDisabled ||
                    uploadStatus === 'loading' ||
                    isSubmitting
                    }
                    className={`text-xs font-bold px-6 py-2.5 rounded-xl transition-all shadow-md active:scale-95 ${
                      showSuccess
                        ? 'bg-emerald-500 text-white'
                        : 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white'
                    }`}
                />
              ) }
            </div>

            { currentQuestion.fileUrl && (
              <div className='flex flex-col gap-3 md:gap-4 h-full overflow-y-auto pr-1 hidden md:flex custom-scrollbar bg-white/50 backdrop-blur-sm rounded-2xl p-3 border border-gray-100'>
                { /* Assessment Progress */ }
                <div className='grid grid-cols-3 gap-4 w-full'>
                  { renderProgress() }
                  { /* Question Count Card */ }
                  { isBehavioral ? (
                    <div
                        className={
                          'w-full mx-auto rounded-2xl border-2 bg-white p-4 text-center transition-all duration-300 shadow-sm border-pink-200 bg-pink-50/30'
                        }
                    >
                      <h3 className='text-xs font-bold text-gray-400 uppercase tracking-widest mb-1'>
                        Behavioural type questions may include 3 follow up
                        questions
                      </h3>
                    </div>
                  ) : (
                    <div
                        className={
                          'w-full mx-auto rounded-2xl border-2 bg-white p-4 text-center transition-all duration-300 shadow-sm border-blue-200 bg-blue-50/30'
                        }
                    >
                      <h3 className='text-xs font-bold text-gray-600 uppercase tracking-widest mb-1'>
                        Skill Questions
                      </h3>
                      <div className={'text-xl font-black mb-1text-blue-600'}>
                        { questionCount <= 1 ? '0' : questionCount - 1 }
                      </div>
                      <p className='text-xs font-bold text-gray-600 uppercase tracking-widest'>
                        Left to answer
                      </p>
                    </div>
                  ) }

                  <div className='bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex-1 min-h-0 overflow-y-auto'>
                    <h4 className='text-xs font-bold text-gray-900 uppercase tracking-widest mb-4 flex items-center gap-2'>
                      <span className='w-6 h-6 flex items-center justify-center bg-green-100 text-green-700 rounded-lg'>
                        <svg
                            className='w-4 h-4'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                        >
                          <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'
                          />
                        </svg>
                      </span>
                      Video Tips
                    </h4>
                    <div className='flex flex-col items-center gap-4 text-[11px] text-gray-600'>
                      <div className='flex items-center gap-2'>
                        <div className='w-1.5 h-1.5 rounded-full bg-green-400' />
                        <span>Stable internet required</span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <div className='w-1.5 h-1.5 rounded-full bg-green-400' />
                        <span>Quiet, well-lit environment</span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <div className='w-1.5 h-1.5 rounded-full bg-green-400' />
                        <span className='font-medium text-green-700'>
                          Re-recording allowed
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                { /* Video Tips */ }
              </div>
            ) }
          </div>
        </div>

        { /* Col 3 (Sidebar) */ }
        { !currentQuestion.fileUrl && (
          <div className='col-span-1 h-full'>{ renderSidebar() }</div>
        ) }
      </div>
    );
  };

  return (
    <div className='w-full mx-auto flex flex-col items-center'>
      { renderQuestionArea() }
    </div>
  );
};
